import React from "react";
import { Box, Checkbox, FormControlLabel, FormGroup, Tooltip, Typography } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { FONT, NEUTRAL, RADIUS, VIOLET } from "../../styles/brandTokens";
import { optionHintSx, optionLabelSx } from "./SettingsSection";
import {
  AUTHORITIES,
  ApprovalRules,
  Authority,
  ConfigurableRequestType,
  REQUEST_ROWS,
  RequestRow,
} from "./authorizationPolicy";

interface ApprovalRulesListProps {
  rules: Readonly<ApprovalRules>;
  onToggle: (requestType: ConfigurableRequestType, authority: Authority) => void;
}

function ApproverCell({ row, rules, onToggle }: { row: RequestRow } & ApprovalRulesListProps) {
  if (row.kind === "fixed") {
    return (
      <Tooltip title="Fixed by policy">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 40 }}>
          <LockOutlinedIcon sx={{ fontSize: 16, color: NEUTRAL.textMuted }} aria-hidden />
          <Typography sx={{ ...optionLabelSx, color: NEUTRAL.textSecondary }}>{row.approver}</Typography>
        </Box>
      </Tooltip>
    );
  }

  const selected = rules[row.value];
  return (
    <FormGroup role="group" aria-label={`Who approves ${row.label}`} sx={{ gap: 0 }}>
      {AUTHORITIES.map((authority) => (
        <FormControlLabel
          key={authority.value}
          control={
            <Checkbox
              size="small"
              checked={selected.includes(authority.value)}
              onChange={() => onToggle(row.value, authority.value)}
              inputProps={{ "aria-label": `${authority.label} for ${row.label}` }}
              sx={{ color: NEUTRAL.borderStrong, "&.Mui-checked": { color: VIOLET.main }, py: 0.25 }}
            />
          }
          label={<Typography sx={{ ...optionLabelSx, fontSize: "13px" }}>{authority.label}</Typography>}
          sx={{ m: 0, alignItems: "center" }}
        />
      ))}
    </FormGroup>
  );
}

/** One row per request type. Configurable rows can allow more than one approving group. */
export default function ApprovalRulesList({ rules, onToggle }: ApprovalRulesListProps) {
  return (
    <Box
      component="ul"
      aria-label="Who approves each request type"
      sx={{ m: 0, p: 0, listStyle: "none", border: `1px solid ${NEUTRAL.border}`, borderRadius: `${RADIUS.card}px` }}
    >
      {REQUEST_ROWS.map((row, index) => (
        <Box
          component="li"
          key={row.value}
          sx={{
            display: "grid",
            gap: { xs: 1, md: 3 },
            gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1fr) 220px" },
            alignItems: "center",
            px: 2,
            py: 1.5,
            borderTop: index === 0 ? "none" : `1px solid ${NEUTRAL.border}`,
            background: row.kind === "fixed" ? NEUTRAL.subtle : NEUTRAL.surface,
          }}
        >
          <Box>
            <Typography sx={{ ...optionLabelSx, fontWeight: 600, fontFamily: FONT.sans }}>{row.label}</Typography>
            <Typography sx={optionHintSx}>{row.description}</Typography>
          </Box>
          <ApproverCell row={row} rules={rules} onToggle={onToggle} />
        </Box>
      ))}
    </Box>
  );
}
